import { useNavigate } from 'react-router';
import { Box } from 'lucide-react';
import { fmtShortDate } from '@/utils/dates';
import {
  DASHBOARD_STATUS_LABELS,
  DASHBOARD_STATUS_STYLE,
  type DashboardOrder,
} from '../lib/dashboardData';
import { SectionHeader } from './SectionHeader';

export interface RecentOrdersPanelProps {
  /** Orders pre-sorted with the most recent first. */
  orders: DashboardOrder[];
  limit?: number;
}

/**
 * "Recent Orders" panel — compact table of the most recently created orders.
 * Each row links into the order detail screen (Phase 5+).
 */
export function RecentOrdersPanel({ orders, limit = 7 }: RecentOrdersPanelProps) {
  const navigate = useNavigate();
  const rows = orders.slice(0, limit);

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <SectionHeader
        icon={Box}
        iconColor="text-blue-500"
        title="Recent Orders"
        onLink={() => navigate('/orders')}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700 text-white">
            <tr>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wider">
                Order #
              </th>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wider">
                Customer
              </th>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
              <th className="px-3 py-1.5 text-center text-xs font-medium uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {rows.map((order) => (
              <tr
                key={order.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <td className="whitespace-nowrap px-3 py-1 text-xs font-semibold text-slate-800">
                  {order.jobNumber}
                </td>
                <td className="max-w-[120px] truncate px-3 py-1 text-xs text-slate-600">
                  {order.customer}
                </td>
                <td className="whitespace-nowrap px-3 py-1 text-xs text-slate-500">
                  {fmtShortDate(order.serviceDate) || '—'}
                </td>
                <td className="px-3 py-1 text-center">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${DASHBOARD_STATUS_STYLE[order.status]}`}
                  >
                    {DASHBOARD_STATUS_LABELS[order.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
